import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, SafeAreaView, ScrollView,
  TouchableOpacity, ActivityIndicator, TextInput, Alert, Modal
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebaseConfig';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { useAdminAuth } from '../hooks/useAdminAuth';

import TournamentSeedingEditor from '../components/TournamentSeedingEditor';

const COLORS = {
  GOLD: '#FCD000',
  BG: '#0b0b0b',
  CARD: '#101010',
  TEXT: '#EDEDED',
  DIM: '#A0A0A0',
  BORDER: '#1a1a1a',
  SUCCESS: '#2ECC71',
  DANGER: '#E74C3C'
};

export default function ManageEventsScreen({ navigation }) {
  const { user, isAdmin, isLoading } = useAdminAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('live'); // 'live', 'scheduled', 'completed', 'all'
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for the seeding editor modal
  const [seedingTournamentId, setSeedingTournamentId] = useState(null);

  useEffect(() => {
    if (!isAdmin || isLoading) return;
    loadEvents();
  }, [isAdmin, isLoading, filter]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      let q;
      
      if (filter === 'all') {
        q = query(
          collection(db, 'tournaments'),
          orderBy('startAt', 'desc'),
          limit(50)
        );
      } else {
        q = query(
          collection(db, 'tournaments'),
          where('status', '==', filter),
          orderBy('startAt', 'desc'),
          limit(50)
        );
      }
      
      const snapshot = await getDocs(q);
      const eventsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setEvents(eventsList);
    } catch (error) {
      console.error('Error loading events:', error);
      Alert.alert('Error', 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEvent = async (eventId) => {
    Alert.alert(
      'Cancel Event',
      'Are you sure? This returns every participant\'s entry Crits.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          style: 'destructive',
          onPress: async () => {
            try {
              const functions = getFunctions();
              const cancelEvent = httpsCallable(functions, 'adminCancelEvent');
              await cancelEvent({ eventId });
              Alert.alert('Success', 'Event cancelled and entry Crits returned');
              loadEvents();
            } catch (error) {
              Alert.alert('Error', error.message);
            }
          }
        }
      ]
    );
  };

  const handleForceComplete = async (eventId) => {
    Alert.alert(
      'Force Complete',
      'Manually complete this event and distribute payouts?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          onPress: async () => {
            try {
              const functions = getFunctions();
              const completeEvent = httpsCallable(functions, 'adminCompleteEvent');
              await completeEvent({ eventId });
              Alert.alert('Success', 'Event completed');
              loadEvents();
            } catch (error) {
              Alert.alert('Error', error.message);
            }
          }
        }
      ]
    );
  };

  if (isLoading || !user) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerWrap}>
          <ActivityIndicator color={COLORS.GOLD} />
        </View>
      </SafeAreaView>
    );
  }

  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.centerWrap}>
          <Text style={styles.deniedText}>Access Denied</Text>
          <TouchableOpacity onPress={() => navigation.navigate('Lobby')}>
            <Text style={styles.backLink}>Go to Lobby</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const filteredEvents = events.filter(event => {
    if (!searchTerm) return true;
    return event.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           event.id.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Manage Events</Text>
          <Text style={styles.subtitle}>Monitor and control all platform events</Text>
        </View>

        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.navigate('AdminDashboard')}
        >
          <Text style={styles.backButtonText}>← Back to Admin Hub</Text>
        </TouchableOpacity>

        {/* Search */}
        <TextInput
          style={styles.searchInput}
          placeholder="Search by name or ID..."
          placeholderTextColor={COLORS.DIM}
          value={searchTerm}
          onChangeText={setSearchTerm}
        />

        {/* Filters */}
        <View style={styles.filterRow}>
          {['live', 'scheduled', 'locked', 'seeded', 'completed', 'all'].map(f => (
            <TouchableOpacity
              key={f}
              style={[styles.filterBtn, filter === f && styles.filterBtnActive]}
              onPress={() => setFilter(f)}
            >
              <Text style={[styles.filterText, filter === f && styles.filterTextActive]}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Events List */}
        {loading ? (
          <ActivityIndicator color={COLORS.GOLD} style={{ marginTop: 20 }} />
        ) : (
          <View style={styles.eventsList}>
            {filteredEvents.length === 0 ? (
              <Text style={styles.emptyText}>No events found</Text>
            ) : (
              filteredEvents.map(event => (
                <View key={event.id} style={styles.eventCard}>
                  <View style={styles.eventHeader}>
                    <Text style={styles.eventName}>{event.name || 'Unnamed Event'}</Text>
                    <View style={[styles.statusBadge, styles[`status_${event.status}`]]}>
                      <Text style={styles.statusText}>{event.status || 'unknown'}</Text>
                    </View>
                  </View>

                  <Text style={styles.eventId}>ID: {event.id}</Text>
                  <Text style={styles.eventDetail}>
                    Entrants: {event.entrantsCount || 0}/{event.entriesExpected || 0}
                  </Text>
                  <Text style={styles.eventDetail}>
                    Prize Pool: ${((event.financials?.prizePool || 0) / 100).toFixed(2)}
                  </Text>

                  <View style={styles.actionRow}>
                    <TouchableOpacity
                      style={styles.actionBtn}
                      onPress={() => navigation.navigate('EventDetailScreen', { eventId: event.id })}
                    >
                      <Text style={styles.actionBtnText}>View Details</Text>
                    </TouchableOpacity>

                    {/* Bracket Management - for tournaments that need seeding */}
                    {['scheduled', 'locked', 'seeded'].includes(event.status) && (
                      <TouchableOpacity
                        style={[styles.actionBtn, styles.actionBtnPrimary]}
                        onPress={() => setSeedingTournamentId(event.id)}
                      >
                        <Text style={styles.actionBtnText}>⚙️ Bracket</Text>
                      </TouchableOpacity>
                    )}

                    {event.status === 'live' && (
                      <TouchableOpacity
                        style={[styles.actionBtn, styles.actionBtnDanger]}
                        onPress={() => handleForceComplete(event.id)}
                      >
                        <Text style={styles.actionBtnText}>Force Complete</Text>
                      </TouchableOpacity>
                    )}

                    {['scheduled', 'locked', 'live'].includes(event.status) && (
                      <TouchableOpacity
                        style={[styles.actionBtn, styles.actionBtnDanger]}
                        onPress={() => handleCancelEvent(event.id)}
                      >
                        <Text style={styles.actionBtnText}>Cancel</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              ))
            )}
          </View>
        )}
      </ScrollView>

      {/* Tournament Seeding Editor Modal */}
      {seedingTournamentId && (
        <Modal
          visible={true}
          animationType="slide"
          onRequestClose={() => setSeedingTournamentId(null)}
        >
          <TournamentSeedingEditor
            tournamentId={seedingTournamentId}
            onClose={() => {
              setSeedingTournamentId(null);
              loadEvents();
            }}
            onSeedingComplete={() => {
              loadEvents();
            }}
          />
        </Modal>
      )}

      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: COLORS.BG },
  container: { flexGrow: 1, paddingHorizontal: 20, paddingVertical: 20 },
  centerWrap: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  header: { marginBottom: 20 },
  title: { fontSize: 28, fontWeight: 'bold', color: COLORS.GOLD, marginBottom: 8 },
  subtitle: { fontSize: 14, color: COLORS.DIM },
  backButton: { marginBottom: 20 },
  backButtonText: { color: COLORS.GOLD, fontSize: 16 },
  searchInput: {
    backgroundColor: COLORS.CARD,
    color: COLORS.TEXT,
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: COLORS.BORDER
  },
  filterRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
    gap: 8
  },
  filterBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: COLORS.CARD,
    borderRadius: 8,
    alignItems: 'center'
  },
  filterBtnActive: {
    backgroundColor: COLORS.GOLD
  },
  filterText: {
    color: COLORS.TEXT,
    fontSize: 12
  },
  filterTextActive: {
    color: COLORS.BG,
    fontWeight: 'bold'
  },
  eventsList: { gap: 15 },
  eventCard: {
    backgroundColor: COLORS.CARD,
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
    marginBottom: 15
  },
  eventHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10
  },
  eventName: {
    color: COLORS.TEXT,
    fontSize: 18,
    fontWeight: 'bold',
    flex: 1
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12
  },
  status_live: { backgroundColor: COLORS.SUCCESS },
  status_scheduled: { backgroundColor: '#3498DB' },
  status_locked: { backgroundColor: '#9B59B6' },
  status_seeded: { backgroundColor: '#F39C12' },
  status_completed: { backgroundColor: COLORS.DIM },
  status_canceled: { backgroundColor: COLORS.DANGER },
  statusText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  eventId: {
    color: COLORS.DIM,
    fontSize: 12,
    marginBottom: 8
  },
  eventDetail: {
    color: COLORS.TEXT,
    fontSize: 14,
    marginBottom: 4
  },
  actionRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 15,
    gap: 8
  },
  actionBtn: {
    backgroundColor: COLORS.GOLD,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center'
  },
  actionBtnPrimary: {
    backgroundColor: '#3498DB'
  },
  actionBtnDanger: {
    backgroundColor: COLORS.DANGER
  },
  actionBtnText: {
    color: '#000',
    fontWeight: 'bold',
    fontSize: 13
  },
  emptyText: {
    color: COLORS.DIM,
    textAlign: 'center',
    marginTop: 40,
    fontSize: 16
  },
  deniedText: {
    color: COLORS.DANGER,
    fontSize: 24,
    marginBottom: 20
  },
  backLink: {
    color: COLORS.GOLD,
    fontSize: 16,
    textDecorationLine: 'underline'
  }
});